import type { ButtonHTMLAttributes } from 'react';

import { cn } from '@/lib/cn';

/**
 * IL BOTTONE. Una primitiva sola, perche' un restyle che cambia la FORMA dei bottoni (I-353, «stile 3b») la cambi
 * in un punto e non in 560. Quattro maniere, ognuna con il suo tipo:
 *
 *   · testo   — `primary` (default) · `secondary` · `ghost` · `danger` · `neutro` (fondo incavato) · `link` (solo
 *               testo, senza fondo ne' padding); taglie `md` (default, quella di sempre), `sm` e `lg` (la barra
 *               d'azione grande: 48, tonda, maiuscola); `forma="pastiglia"` (tonda); `peso="bold"` (v. sotto).
 *               E' il bottone con un'etichetta.
 *   · `icona` — SOLO icona. `aria-label` e' OBBLIGATORIO nel tipo: un bottone senza testo senza nome non compila.
 *               Disegno 24 / 28 / 32 / 36 / 40 (`size`, default 40); `tono` `trasparente` (default) o `pieno`
 *               (fondo `bg-secondary`, come i «chiudi» dei fogli) o `neutro` (fondo `bg-tertiary`, testo smorzato).
 *   · `scelta` — acceso/spento (pastiglie di filtro, selettori, segmenti). `pressed` e' OBBLIGATORIO nel tipo e
 *               diventa `aria-pressed`. `forma` `pastiglia` (default, tonda) o `tessera` (rettangolo arrotondato,
 *               per i segmenti e le opzioni); `tono` `pieno` (default: acceso = fondo teal), `tenue` (acceso =
 *               contorno e velo teal) o `incavo` (senza bordo, spenta su `bg-tertiary`: i fogli di sfida);
 *               `peso="bold"`. NON e' per le schede (`role="tab"`: `Segmented`, `UnderlineTabs`) ne' per gli
 *               interruttori veri (`Interruttore`): `role` non e' fra le proprieta'.
 *
 * AREA DI TOCCO ≥ 44 di default, senza cambiare il DISEGNO: `md` e' gia' 44 (py-3 su una riga da 20); le taglie
 * piu' piccole (`sm`, `icona`, `scelta`) estendono l'area con un pseudo-elemento centrato, alto e largo almeno
 * 2,75rem, che non sposta nulla nel layout. ⚠️ Due aree di tocco vicine NON devono sovrapporsi: dove si
 * sovrapporrebbero il chiamante riduce l'estensione con una classe (`after:h-9`, `after:w-9`: twMerge la fa
 * vincere) e il caso si dichiara con la misura nella PR che lo porta. Le catture di ogni PR di migrazione lo
 * controllano; il test della primitiva (`tests/button-primitiva.test.tsx`) tiene ferme le classi.
 *
 * Un asse nuovo entra SOLO se lo usano almeno 3 bottoni della zona che lo porta; altrimenti il bottone resta a
 * mano, col suo perche' (decisione di tma-orcb, 04/10).
 *
 * Le proprieta' del chiamante (`className`, `onClick`, `disabled`…) passano com'erano. `type="button"` e' il
 * default, e un `type` esplicito lo scavalca.
 */
type Nativi = ButtonHTMLAttributes<HTMLButtonElement>;

export type ButtonTestoProps = Nativi & {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'neutro' | 'link';
  /** `lg` = la barra d'azione grande (alta 48, tonda, maiuscola): CTA di fondo schermo e dei fogli di partita. */
  size?: 'md' | 'sm' | 'lg';
  fullWidth?: boolean;
  /** `pastiglia` = tonda (`rounded-full`) al posto di `rounded-xl`. */
  forma?: 'pastiglia';
  peso?: 'bold';
  /** Solo del `link`: `maiuscoletto` = `text-[11px] font-bold uppercase tracking-wide` (le azioni di testo dei pannelli della Hub). */
  corpo?: 'maiuscoletto';
};

export type ButtonIconaProps = Omit<Nativi, 'aria-label'> & {
  variant: 'icona';
  'aria-label': string;
  size?: 24 | 28 | 32 | 36 | 40;
  tono?: 'trasparente' | 'pieno' | 'neutro';
};

export type ButtonSceltaProps = Omit<Nativi, 'aria-pressed' | 'role'> & {
  variant: 'scelta';
  pressed: boolean;
  forma?: 'pastiglia' | 'tessera';
  tono?: 'pieno' | 'tenue' | 'incavo';
  peso?: 'bold';
};

export type ButtonProps = ButtonTestoProps | ButtonIconaProps | ButtonSceltaProps;

/** La forma interna: l'unione di sopra, aperta, per poterla leggere. Il tipo PUBBLICO resta l'unione chiusa. */
type Interno = Nativi & {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'neutro' | 'link' | 'icona' | 'scelta';
  size?: 'md' | 'sm' | 'lg' | 24 | 28 | 32 | 36 | 40;
  fullWidth?: boolean;
  pressed?: boolean;
  forma?: 'pastiglia' | 'tessera';
  tono?: 'trasparente' | 'pieno' | 'neutro' | 'tenue' | 'incavo';
  peso?: 'bold';
  corpo?: 'maiuscoletto';
};

/** L'estensione dell'area di tocco: almeno 44 per lato, centrata sul disegno, trasparente, fuori dal layout. */
const AREA_DI_TOCCO =
  "relative after:absolute after:left-1/2 after:top-1/2 after:h-[max(100%,2.75rem)] after:w-[max(100%,2.75rem)] after:-translate-x-1/2 after:-translate-y-1/2 after:content-['']";

const DISEGNO_ICONA = {
  24: 'size-6',
  28: 'size-7',
  32: 'size-8',
  36: 'size-9',
  40: 'size-10',
} as const;
type TagliaIcona = keyof typeof DISEGNO_ICONA;

const FORMA_SCELTA = {
  pastiglia: 'rounded-full px-3 py-1.5 text-xs',
  tessera: 'rounded-xl px-3 py-2 text-sm',
} as const;

export function Button(props: ButtonProps) {
  const {
    variant = 'primary',
    size,
    fullWidth,
    pressed,
    forma,
    tono,
    peso,
    corpo,
    className,
    children,
    ...rest
  } = props as Interno;
  const testo = variant !== 'icona' && variant !== 'scelta';
  const link = variant === 'link';
  return (
    <button
      type="button"
      // Marca del bottone COMUNE (non della v3): e' l'attributo con cui la forma asimmetrica della v3 lo riconosce e
      // lascia ai `<button>` scritti a mano la forma di prima (ADR-0192 §4). Senza la v3 non cambia niente.
      data-qbtn=""
      className={cn(
        'inline-flex items-center justify-center transition-[opacity,background-color,color] active:opacity-80 disabled:opacity-50',
        testo && !link && size !== 'sm' && size !== 'lg' && 'gap-2 rounded-xl px-4 py-3 text-sm font-semibold',
        testo &&
          !link &&
          size === 'sm' &&
          `gap-1.5 rounded-xl px-3 py-1.5 text-xs font-semibold ${AREA_DI_TOCCO}`,
        /* `lg`: la barra d'azione grande (PR-3: i primari di `LimuloMatchActions`, «Entra al tavolo», `MainCta`).
           Alta 48 per costruzione (nessuna estensione dell'area: e' gia' ≥ 44), tonda, `font-bold` e maiuscola con
           spaziatura: e' la forma di quella barra, non una taglia fra le altre. */
        testo &&
          !link &&
          size === 'lg' &&
          'h-12 gap-2 rounded-full px-4 text-base font-bold uppercase tracking-wide',
        /* `pastiglia` sul testo: la stessa pastiglia tonda della `scelta`, per i «Riprova/Annulla» e le azioni di riga
           (7 bottoni di social, PR-2). Dopo le taglie: twMerge fa vincere `rounded-full` su `rounded-xl`. */
        testo && forma === 'pastiglia' && 'rounded-full',
        /* 🔴 Il primario SPENTO prende fondo e testo neutri, e al posto dell'opacita' a meta'. Il teal
           pieno al 50% su una barra teal resta un teal pieno e invita al tocco: su stg-tma (29/09)
           «Iscriviti» spento sembrava acceso accanto a un «Check-in» spento che si leggeva spento.
           ⚠️ Opacita' PIENA, non 50: neutro PIU' meta' opacita' scendeva a 2,1:1, neutro pieno sta a
           ~4:1 (e `MatchDetail.tsx` usava gia' questa ricetta). `cn` (twMerge) toglie il 50 di sopra.
           ⚠️ Vale anche mentre una richiesta e' in volo: spento e' spento, e l'etichetta dice «Invio…».
           Token gia' esistenti, anche nei temi. Divieti: `tools/verify-bottoni.ts`; garanzie
           (cio' che fa twMerge): `tests/bottone-spento-neutro.test.tsx`. */
        variant === 'primary' &&
          'bg-button text-button-text disabled:bg-bg-tertiary disabled:text-fg-muted disabled:opacity-100',
        variant === 'secondary' && 'bg-bg-secondary text-text',
        variant === 'ghost' && 'bg-transparent text-link',
        /* NEUTRO: l'azione di contorno («Annulla», «Riprova», «Sfida un amico») su superficie incavata. Non e' `secondary`
           (`bg-bg-secondary text-text`): il fondo e il testo sono altri token, e una migrazione e' identica al pixel. */
        variant === 'neutro' && 'bg-bg-tertiary text-fg-muted',
        /* LINK: solo testo, ne' fondo ne' padding; il disegno e' la riga di testo, l'area di tocco 44 viene dal
           pseudo-elemento. Sottolineatura, colore smorzato e corpo diverso sono del chiamante (`underline`,
           `text-fg-muted`): la primitiva dice solo «e' un link». `size` non conta. */
        link && `gap-1.5 bg-transparent p-0 text-xs font-semibold text-link ${AREA_DI_TOCCO}`,
        /* `corpo="maiuscoletto"` (PR-4, 6 link della Hub): il corpo piccolo, grassetto e maiuscolo con spaziatura che i
           pannelli dei mazzi scrivono uguale in sei punti. Dopo la base del link: twMerge fa vincere questi sul `text-xs
           font-semibold`. Un asse e non sei override ripetuti: il restyle lo cambia in una riga. */
        link && corpo === 'maiuscoletto' && 'text-[11px] font-bold uppercase tracking-wide',
        /* ⚠️ L'azione DISTRUTTIVA non eredita il bottone pieno: contorno, non fondo. Sta
           nello stesso posto di quella costruttiva e ha lo stesso peso di lettura, ma non
           lo stesso peso d'invito — un ritiro premuto per abitudine e' il difetto che
           questa variante esiste per evitare. (Reference scheda torneo B1, punto 7.) */
        variant === 'danger' && 'border border-danger bg-transparent text-danger',
        /* ICONA: nessun testo, quindi nessun padding; il disegno e' un cerchio di 24…40 e l'area di tocco 44. */
        variant === 'icona' &&
          `shrink-0 rounded-full ${DISEGNO_ICONA[(size as TagliaIcona | undefined) ?? 40]} ${AREA_DI_TOCCO}`,
        variant === 'icona' &&
          (tono === 'pieno'
            ? 'bg-bg-secondary text-fg'
            : tono === 'neutro'
              ? 'bg-bg-tertiary text-fg-muted'
              : 'bg-transparent text-fg-muted'),
        /* SCELTA: la pastiglia o la tessera. Accesa = fondo teal (e `aria-pressed="true"` la esclude dalla regola
           globale del bottone pieno spento: in volo resta teal) oppure, `tenue`, contorno e velo teal; spenta =
           superficie con contorno. ⚠️ Niente feedback di pressione (`active:opacity-100` toglie quello della base):
           le scelte di oggi non l'hanno, e una migrazione e' identica al pixel. Uniformarlo e' una scelta del restyle,
           e si fa qui, in un punto. */
        variant === 'scelta' &&
          `border font-semibold transition-[opacity,background-color,border-color,color] active:opacity-100 ${FORMA_SCELTA[forma ?? 'pastiglia']} ${AREA_DI_TOCCO}`,
        /* `incavo` (PR-2): le scelte dei fogli di sfida. SENZA bordo (`border-0` vince su `border`: il bordo di 1px
           alzerebbe la scelta di 2px), spenta su `bg-bg-tertiary`, accesa = fondo teal pieno. Non e' `tenue` (accesa =
           contorno e velo) ne' `pieno` (spenta con contorno su `bg-bg-secondary`): e' un terzo disegno. */
        variant === 'scelta' &&
          (tono === 'incavo'
            ? pressed
              ? 'border-0 bg-brand text-brand-fg'
              : 'border-0 bg-bg-tertiary text-fg-muted'
            : tono === 'tenue'
              ? pressed
                ? 'border-brand bg-brand/10 text-fg'
                : 'border-border bg-bg-secondary text-fg-muted'
              : pressed
                ? 'border-brand bg-brand text-brand-fg'
                : 'border-border bg-bg-secondary text-fg'),
        testo && fullWidth && 'w-full',
        /* `peso: 'bold'` (PR-2): un'incoerenza di OGGI conservata al pixel — 25 bottoni di social scrivono `font-bold`
           dove la primitiva dice `font-semibold`. Da togliere al restyle, in questa riga. Un asse e non un override
           del chiamante perche' il `font-bold` passato da fuori vince sulla primitiva e il restyle non lo raggiunge. */
        variant !== 'icona' && peso === 'bold' && 'font-bold',
        className,
      )}
      {...rest}
      {...(variant === 'scelta' ? { 'aria-pressed': pressed === true } : {})}
    >
      {children}
    </button>
  );
}
